import React, { useEffect, useRef, useState } from 'react';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { ConfirmModal } from '../../../shared/components/ConfirmModal';
import { Spinner } from '../../../shared/components/Spinner';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { useAuth } from '../../../contexts/AuthContext';
import type { AssignmentRow, FeatureFlag, FeatureFlagDetail } from '../../../models/featureFlag';
import { fetchAdminDashboard } from '../../../services/admin/dashboardService';
import {
  describeFeatureFlagEffectiveStatus,
  fetchFeatureFlagAssignmentTargets,
  fetchFeatureFlagDetail,
  getFeatureFlagEffectiveStatus,
  logFeatureFlagAudit,
  updateFeatureFlag,
  type FeatureFlagEffectiveStatus,
} from '../../../services/admin/featureFlagService';

const EFFECTIVE_STATUS_CLASS: Record<FeatureFlagEffectiveStatus, string> = {
  global: 'bo-status-active',
  targeted: 'bo-status-targeted',
  inactive: 'bo-status-inactive',
};

const EFFECTIVE_STATUS_EXPLANATION: Record<FeatureFlagEffectiveStatus, string> = {
  global: 'Enabled for everyone.',
  targeted: 'Enabled only for the assignments below.',
  inactive: 'Not enabled for anyone yet — enable it globally from the list, or add an assignment below.',
};

export interface FeatureFlagAssignmentsPanelProps {
  visible: boolean;
  flag: FeatureFlag | null;
  onClose: () => void;
  /** Called after any assignment change is saved, so the parent list can refresh its counts. */
  onChanged: () => void;
}

type Tab = 'companies' | 'users' | 'roles';

interface RemoveRequest {
  tab: Tab;
  id: string;
  label: string;
}

interface CompanyOption {
  value: string;
  text: string;
}

export function FeatureFlagAssignmentsPanel({
  visible,
  flag,
  onClose,
  onChanged,
}: FeatureFlagAssignmentsPanelProps): React.ReactElement | null {
  const { getAccessToken, acquireAccessToken } = useAuth();
  const panelRef = useRef<HTMLDivElement>(null);

  const [tab, setTab] = useState<Tab>('companies');
  const [detail, setDetail] = useState<FeatureFlagDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
  const [companyOptionsLoading, setCompanyOptionsLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(undefined);

  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<AssignmentRow[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  const [selectedRole, setSelectedRole] = useState<string | undefined>(undefined);

  const [removeRequest, setRemoveRequest] = useState<RemoveRequest | null>(null);

  useEffect(() => {
    if (!visible || !flag) {
      return;
    }
    setTab('companies');
    setActionError(null);
    setLoadError(null);
    setUserSearch('');
    setUserResults([]);
    setSelectedCompanyId(undefined);
    setSelectedRole(undefined);
    setLoading(true);
    fetchFeatureFlagDetail(flag.id, getAccessToken)
      .then((result) => setDetail(result))
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'Failed to load assignments.'))
      .finally(() => setLoading(false));

    setCompanyOptionsLoading(true);
    fetchAdminDashboard(acquireAccessToken, { status: 'active' })
      .then((companies) =>
        setCompanyOptions(companies.map((c) => ({ value: c.companyId, text: c.companyName })))
      )
      .catch(() => setCompanyOptions([]))
      .finally(() => setCompanyOptionsLoading(false));
  }, [visible, flag, getAccessToken, acquireAccessToken]);

  useEffect(() => {
    if (!visible || tab !== 'users') {
      return;
    }
    const handle = setTimeout(() => {
      setUserSearchLoading(true);
      fetchFeatureFlagAssignmentTargets('user', userSearch, getAccessToken)
        .then((rows) => setUserResults(rows))
        .catch(() => setUserResults([]))
        .finally(() => setUserSearchLoading(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [visible, tab, userSearch, getAccessToken]);

  if (!visible || !flag) {
    return null;
  }

  const archived = flag.isArchived;
  const totalAssigned = detail ? detail.companies.length + detail.users.length + detail.roles.length : 0;
  const effectiveStatus = detail
    ? getFeatureFlagEffectiveStatus(archived, detail.enabledGlobally, totalAssigned)
    : null;

  const saveList = async (
    field: 'enabledForCompanies' | 'enabledForUsers' | 'enabledForRoles',
    ids: string[]
  ): Promise<void> => {
    setActionError(null);
    setSaving(true);
    try {
      await updateFeatureFlag(flag.id, { [field]: ids }, getAccessToken);
      logFeatureFlagAudit(field === 'enabledForRoles' ? 'update' : ids.length > (detail ? currentIdsFor(field, detail).length : 0) ? 'assign' : 'unassign', {
        id: flag.id,
        field,
      });
      onChanged();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save the assignment change.');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const currentIdsFor = (field: 'enabledForCompanies' | 'enabledForUsers' | 'enabledForRoles', d: FeatureFlagDetail): string[] => {
    if (field === 'enabledForCompanies') return d.companies.map((c) => c.id);
    if (field === 'enabledForUsers') return d.users.map((u) => u.id);
    return d.roles;
  };

  const handleAddCompany = async (): Promise<void> => {
    if (!detail || !selectedCompanyId) {
      return;
    }
    const option = companyOptions.find((c) => c.value === selectedCompanyId);
    const nextIds = [...detail.companies.map((c) => c.id), selectedCompanyId];
    try {
      await saveList('enabledForCompanies', nextIds);
      setDetail({
        ...detail,
        companies: [...detail.companies, { id: selectedCompanyId, displayName: option?.text ?? selectedCompanyId }],
      });
      setSelectedCompanyId(undefined);
    } catch {
      /* handled in saveList */
    }
  };

  const handleAddUser = async (row: AssignmentRow): Promise<void> => {
    if (!detail) {
      return;
    }
    const nextIds = [...detail.users.map((u) => u.id), row.id];
    try {
      await saveList('enabledForUsers', nextIds);
      setDetail({ ...detail, users: [...detail.users, row] });
    } catch {
      /* handled in saveList */
    }
  };

  const handleAddRole = async (): Promise<void> => {
    if (!detail || !selectedRole) {
      return;
    }
    const nextRoles = [...detail.roles, selectedRole];
    try {
      await saveList('enabledForRoles', nextRoles);
      setDetail({ ...detail, roles: nextRoles });
      setSelectedRole(undefined);
    } catch {
      /* handled in saveList */
    }
  };

  const handleConfirmRemove = async (): Promise<void> => {
    if (!detail || !removeRequest) {
      return;
    }
    if (removeRequest.tab === 'companies') {
      const nextIds = detail.companies.filter((c) => c.id !== removeRequest.id).map((c) => c.id);
      await saveList('enabledForCompanies', nextIds);
      setDetail({ ...detail, companies: detail.companies.filter((c) => c.id !== removeRequest.id) });
    } else if (removeRequest.tab === 'users') {
      const nextIds = detail.users.filter((u) => u.id !== removeRequest.id).map((u) => u.id);
      await saveList('enabledForUsers', nextIds);
      setDetail({ ...detail, users: detail.users.filter((u) => u.id !== removeRequest.id) });
    } else {
      const nextRoles = detail.roles.filter((r) => r !== removeRequest.id);
      await saveList('enabledForRoles', nextRoles);
      setDetail({ ...detail, roles: nextRoles });
    }
  };

  const assignedCompanyIds = new Set(detail?.companies.map((c) => c.id) ?? []);
  const assignedUserIds = new Set(detail?.users.map((u) => u.id) ?? []);
  const availableCompanyOptions = companyOptions.filter((c) => !assignedCompanyIds.has(c.value));
  const availableUserResults = userResults.filter((u) => !assignedUserIds.has(u.id));
  const availableRoles = (detail?.availableRoles ?? []).filter((r) => !(detail?.roles ?? []).includes(r));

  const renderChip = (tabKind: Tab, row: { id: string; label: string }): React.ReactElement => (
    <span key={row.id} className="bo-admin-order-chip bo-admin-order-chip-removable">
      {row.label}
      <button
        type="button"
        className="bo-admin-chip-remove"
        aria-label={`Remove ${row.label}`}
        disabled={saving}
        onClick={() => setRemoveRequest({ tab: tabKind, id: row.id, label: row.label })}
      >
        ×
      </button>
    </span>
  );

  return (
    <>
    <div
      className="bo-modal-backdrop"
      role="presentation"
      onClick={() => {
        if (!saving) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        className="bo-modal-panel"
        role="dialog"
        aria-modal
        aria-labelledby="bo-feature-flag-assignments-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="bo-feature-flag-assignments-title" className="bo-modal-title">
          Manage assignments · {flag.key}
        </h2>

        {effectiveStatus ? (
          <p className="bo-muted" style={{ marginTop: 0 }}>
            <span className={`bo-status-pill ${EFFECTIVE_STATUS_CLASS[effectiveStatus]}`}>
              {describeFeatureFlagEffectiveStatus(effectiveStatus, totalAssigned)}
            </span>{' '}
            {EFFECTIVE_STATUS_EXPLANATION[effectiveStatus]}
          </p>
        ) : null}

        {archived ? (
          <p className="bo-modal-error" role="alert">
            This feature flag is archived. Unarchive it before changing assignments.
          </p>
        ) : null}

        {loading ? <Spinner label="Loading assignments…" /> : null}
        {!loading && loadError ? <ErrorAlert message={loadError} /> : null}

        {!loading && !loadError && detail ? (
          <>
            <div className="bo-assignments-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'companies'}
                className={`bo-assignments-tab ${tab === 'companies' ? 'bo-assignments-tab-active' : ''}`}
                onClick={() => setTab('companies')}
              >
                Companies ({detail.companies.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'users'}
                className={`bo-assignments-tab ${tab === 'users' ? 'bo-assignments-tab-active' : ''}`}
                onClick={() => setTab('users')}
              >
                Users ({detail.users.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'roles'}
                className={`bo-assignments-tab ${tab === 'roles' ? 'bo-assignments-tab-active' : ''}`}
                onClick={() => setTab('roles')}
              >
                Roles ({detail.roles.length})
              </button>
            </div>

            {actionError ? (
              <p className="bo-modal-error" role="alert">
                {actionError}
              </p>
            ) : null}

            {tab === 'companies' ? (
              <div>
                {!archived ? (
                  <div className="bo-actions" style={{ marginBottom: '0.75rem' }}>
                    {companyOptionsLoading ? (
                      <Spinner label="Loading companies…" />
                    ) : (
                      <DropDownListComponent
                        id="feature-flag-add-company"
                        dataSource={availableCompanyOptions as unknown as { [key: string]: Object }[]}
                        fields={{ text: 'text', value: 'value' }}
                        placeholder="Select a company to add"
                        allowFiltering
                        filterBarPlaceholder="Search companies"
                        popupHeight="240px"
                        value={selectedCompanyId}
                        change={(args) => setSelectedCompanyId(args.value != null && args.value !== '' ? String(args.value) : undefined)}
                        cssClass="bo-invite-company-ddl"
                      />
                    )}
                    <button
                      type="button"
                      className="bo-btn bo-btn-primary"
                      disabled={!selectedCompanyId || saving}
                      onClick={() => void handleAddCompany()}
                    >
                      Add
                    </button>
                  </div>
                ) : null}
                <div className="bo-admin-order-mix">
                  {detail.companies.length === 0 ? (
                    <span className="bo-muted">No companies assigned.</span>
                  ) : (
                    detail.companies.map((c) => renderChip('companies', { id: c.id, label: c.displayName }))
                  )}
                </div>
              </div>
            ) : null}

            {tab === 'users' ? (
              <div>
                {!archived ? (
                  <>
                    <label className="bo-field" htmlFor="feature-flag-user-search">
                      <span className="bo-field-label">Search users</span>
                      <input
                        id="feature-flag-user-search"
                        className="bo-input"
                        placeholder="Name or email"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                      />
                    </label>
                    {userSearchLoading ? (
                      <Spinner label="Searching…" />
                    ) : availableUserResults.length > 0 ? (
                      <ul className="bo-admin-order-mix" style={{ listStyle: 'none', padding: 0 }}>
                        {availableUserResults.map((row) => (
                          <li key={row.id}>
                            <button
                              type="button"
                              className="bo-btn bo-btn-secondary"
                              disabled={saving}
                              onClick={() => void handleAddUser(row)}
                            >
                              + {row.displayName}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="bo-muted">No matching active users found.</p>
                    )}
                  </>
                ) : null}
                <div className="bo-admin-order-mix" style={{ marginTop: '0.75rem' }}>
                  {detail.users.length === 0 ? (
                    <span className="bo-muted">No users assigned.</span>
                  ) : (
                    detail.users.map((u) => renderChip('users', { id: u.id, label: u.displayName }))
                  )}
                </div>
              </div>
            ) : null}

            {tab === 'roles' ? (
              <div>
                {!archived ? (
                  <div className="bo-actions" style={{ marginBottom: '0.75rem' }}>
                    <select
                      className="bo-input"
                      value={selectedRole ?? ''}
                      onChange={(e) => setSelectedRole(e.target.value || undefined)}
                    >
                      <option value="">Select a role to add</option>
                      {availableRoles.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="bo-btn bo-btn-primary"
                      disabled={!selectedRole || saving}
                      onClick={() => void handleAddRole()}
                    >
                      Add
                    </button>
                  </div>
                ) : null}
                <div className="bo-admin-order-mix">
                  {detail.roles.length === 0 ? (
                    <span className="bo-muted">No roles assigned.</span>
                  ) : (
                    detail.roles.map((r) => renderChip('roles', { id: r, label: r }))
                  )}
                </div>
              </div>
            ) : null}
          </>
        ) : null}

        <div className="bo-actions" style={{ marginTop: '1.25rem', justifyContent: 'flex-end' }}>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>

    <ConfirmModal
      visible={removeRequest !== null}
      title="Remove assignment"
      message={removeRequest ? `Remove “${removeRequest.label}” from this feature flag?` : ''}
      confirmLabel="Remove"
      confirmVariant="danger"
      onClose={() => setRemoveRequest(null)}
      onConfirm={async () => {
        await handleConfirmRemove();
        setRemoveRequest(null);
      }}
    />
    </>
  );
}
